import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useDoctor } from "../context/DoctorContext";
import { ArrowLeft, MapPin, Star, GripVertical } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import doctorImage from '../images/doctor_image.png';

const SortableItem = ({
  id,
  appointment,
  index,
  renderTimeSlotDropdown,
  handleCheckInChange,
  handleDateChange,
  handleLocationChange,
  handleStatusChange,
  isPastDate,
  convertTo12Hour,
  previousAppointmentDate,
  handleUpdateSingleAppointment, // Add this prop
  isUpdatingSingle,
  isFirstAppointment,
  fieldErrors
}) => {

  const appointmentDate = appointment.date ? new Date(appointment.date) : null;
  const showDateSeparator = isFirstAppointment || (previousAppointmentDate &&
    appointmentDate?.toDateString() !== previousAppointmentDate?.toDateString());

  // Add check for both completed and past appointments
  const isCompleted = appointment.status === "Completed";
  const isPastAppointment = appointmentDate && isPastDate(appointmentDate);
  const isDisabled = isCompleted || isPastAppointment;

  return (
    <>
      {showDateSeparator && appointmentDate && (
        <tr>
          <td colSpan="8" className="py-2 sm:py-4">
            <div className="border-b-2 border-gray-300 pb-2 text-base sm:text-lg font-semibold text-gray-700">
              {appointmentDate.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
          </td>
        </tr>
      )}
      <tr className="block sm:table-row border rounded-lg sm:border-0 shadow-sm sm:shadow-none mb-4 sm:mb-0 bg-white">
        <td className="block sm:table-cell py-2 px-3 sm:py-3 sm:px-4 border-b sm:border-b-0">
          <span className="inline-block sm:hidden font-medium text-gray-500 mb-1">Patient:</span>
          <span className="block sm:inline">{appointment.patientName}</span>
        </td>
        <td className="block sm:table-cell py-2 px-3 sm:py-3 sm:px-4 border-b sm:border-b-0">
          <span className="inline-block sm:hidden font-medium text-gray-500 mb-1">Date:</span>
          <div className="relative">
            <input
              type="text"
              value={appointmentDate ? `${String(appointmentDate.getDate()).padStart(2, '0')}/${String(appointmentDate.getMonth() + 1).padStart(2, '0')}/${appointmentDate.getFullYear()}` : ''}
              readOnly
              className="border rounded px-2 py-1 w-full sm:w-32 bg-gray-100"
            />
          </div>
        </td>
        {/* Start Time and End Time side by side */}
        <td className="block sm:table-cell py-2 px-3 sm:py-3 sm:px-4 border-b sm:border-b-0">
          <div className="flex flex-col sm:block">
            <div className="grid grid-cols-2 gap-2 sm:hidden">
              <div>
                <span className="block font-medium text-gray-500 mb-1">Start Time:</span>
                {appointmentDate && isPastDate(appointmentDate) ? (
                  <div className="text-gray-500 italic">Past appointment</div>
                ) : (
                  renderTimeSlotDropdown(appointment, index)
                )}
              </div>
              <div>
                <span className="block font-medium text-gray-500 mb-1">End Time:</span>
                {appointmentDate && isPastDate(appointmentDate) ? (
                  <div className="text-gray-500 italic">Past appointment</div>
                ) : (
                  <input
                    type="text"
                    value={appointment.endTime || ""}
                    readOnly
                    className="border rounded px-2 py-1 w-full sm:w-24 bg-gray-100"
                    placeholder="End Time"
                  />
                )}
              </div>
            </div>
            <div className="hidden sm:block">
              <span className="inline-block sm:hidden font-medium text-gray-500 mb-1">Start Time:</span>
              {appointmentDate && isPastDate(appointmentDate) ? (
                <div className="text-gray-500 italic">Past appointment</div>
              ) : (
                renderTimeSlotDropdown(appointment, index)
              )}
            </div>
          </div>
        </td>
        <td className="hidden sm:table-cell py-2 px-3 sm:py-3 sm:px-4 border-b sm:border-b-0">
          <span className="inline-block sm:hidden font-medium text-gray-500 mb-1">End Time:</span>
          {appointmentDate && isPastDate(appointmentDate) ? (
            <div className="text-gray-500 italic">Past appointment</div>
          ) : (
            <input
              type="text"
              value={appointment.endTime || ""}
              readOnly
              className="border rounded px-2 py-1 w-full sm:w-24 bg-gray-100"
              placeholder="End Time"
            />
          )}
        </td>

        {/* Latitude and Longitude side by side */}
        <td className="block sm:table-cell py-2 px-3 sm:py-3 sm:px-4 border-b sm:border-b-0">
          <div className="flex flex-col sm:block">
            <div className="grid grid-cols-2 gap-2 sm:hidden">
              <div>
                <span className="block font-medium text-gray-500 mb-1">Latitude:</span>
                <input
                  type="text"
                  value={appointment.latitude || ""}
                  onChange={(e) => handleLocationChange(index, 'latitude', e.target.value)}
                  disabled={isDisabled}
                  className={`border rounded px-2 py-1 w-full transition-colors duration-200 ${isDisabled ? "bg-gray-100 cursor-not-allowed" : "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    }`}
                  placeholder="Latitude"
                />
                {fieldErrors.index === index && fieldErrors.latitude && (
                  <div className="text-red-500 text-xs mt-1">{fieldErrors.latitude}</div>
                )}

              </div>
              <div>
                <span className="block font-medium text-gray-500 mb-1">Longitude:</span>
                <input
                  type="text"
                  value={appointment.longitude || ""}
                  onChange={(e) => handleLocationChange(index, 'longitude', e.target.value)}
                  disabled={isDisabled}
                  className={`border rounded px-2 py-1 w-full transition-colors duration-200 ${isDisabled ? "bg-gray-100 cursor-not-allowed" : "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    }`}
                  placeholder="Longitude"
                />
                {fieldErrors.index === index && fieldErrors.longitude && (
                  <div className="text-red-500 text-xs mt-1">{fieldErrors.longitude}</div>
                )}
              </div>
            </div>
            <div className="hidden sm:block">
              <span className="inline-block sm:hidden font-medium text-gray-500 mb-1">Latitude:</span>
              <input
                type="text"
                value={appointment.latitude || ""}
                onChange={(e) => handleLocationChange(index, 'latitude', e.target.value)}
                disabled={isDisabled}
                className={`border rounded px-2 py-1 w-full sm:w-24 transition-colors duration-200 ${isDisabled ? "bg-gray-100 cursor-not-allowed" : "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  }`}
                placeholder="Latitude"
              />
              {fieldErrors.index === index && fieldErrors.latitude && (
                <div className="text-red-500 text-xs mt-1">{fieldErrors.latitude}</div>
              )}

            </div>
          </div>
        </td>
        <td className="hidden sm:table-cell py-2 px-3 sm:py-3 sm:px-4 border-b sm:border-b-0">
          <span className="inline-block sm:hidden font-medium text-gray-500 mb-1">Longitude:</span>
          <input
            type="text"
            value={appointment.longitude || ""}
            onChange={(e) => handleLocationChange(index, 'longitude', e.target.value)}
            disabled={isDisabled}
            className={`border rounded px-2 py-1 w-full sm:w-24 transition-colors duration-200 ${isDisabled ? "bg-gray-100 cursor-not-allowed" : "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              }`}
            placeholder="Longitude"
          />
          {fieldErrors.index === index && fieldErrors.longitude && (
            <div className="text-red-500 text-xs mt-1">{fieldErrors.longitude}</div>
          )}
        </td>
        <td className="block sm:table-cell py-2 px-3 sm:py-3 sm:px-4 border-b sm:border-b-0">
          <span className="inline-block sm:hidden font-medium text-gray-500 mb-1">Check-in:</span>
          <button
            onClick={() => handleCheckInChange(index)}
            disabled={isDisabled}
            className={`w-full sm:w-auto px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${appointment.checkIn
              ? "bg-green-100 text-green-800 hover:bg-green-200"
              : "bg-red-100 text-red-800 hover:bg-red-200"
              } ${isDisabled ? "opacity-50 cursor-not-allowed" : "hover:shadow-sm"}`}
          >
            {appointment.checkIn ? "True" : "False"}
          </button>
        </td>
        <td className="block sm:table-cell py-2 px-3 sm:py-3 sm:px-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
            <div className="w-full sm:w-auto">
              <span className="inline-block sm:hidden font-medium text-gray-500 mb-1">Status:</span>
              <select
                value={appointment.status}
                onChange={(e) => handleStatusChange(index, e.target.value)}
                disabled={isPastAppointment || appointment.status === "Completed"}
                className={`w-full sm:w-auto px-3 py-1.5 rounded-full text-sm font-medium border transition-all duration-200 ${appointment.status === "Completed"
                  ? "bg-green-100 text-green-800"
                  : appointment.status === "Active"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-yellow-100 text-yellow-800"
                  } ${(isPastAppointment || appointment.status === "Completed") ? "opacity-50 cursor-not-allowed" : "hover:shadow-sm"}`}
              >
                {["Completed", "Active", "Pending"].map((option) => (
                  <option
                    key={option}
                    value={option}
                    disabled={
                      (option === "Completed" && !appointment.checkIn) ||
                      (option === "Completed" && (!appointment.latitude || !appointment.longitude || !appointment.startTime || !appointment.endTime))
                    }
                    className="bg-white text-gray-800"
                  >
                    {option}
                  </option>
                ))}
              </select>
            </div>
            {!isPastAppointment && (
              <div className="w-full sm:w-auto">
                <button
                  onClick={() => handleUpdateSingleAppointment(index)}
                  disabled={isUpdatingSingle === index}
                  className={`w-full sm:w-24 px-2 py-1.5 rounded text-xs sm:text-sm font-medium ${isUpdatingSingle === index
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-blue-500 hover:bg-blue-600 text-white"
                    }`}
                >
                  {isUpdatingSingle === index ? "Updating..." : "Update"}
                </button>
                {fieldErrors.index === index && fieldErrors.general && (
                  <div className="text-red-500 text-xs mt-1 col-span-full">
                    {fieldErrors.general}
                  </div>
                )}
              </div>
            )}
          </div>
        </td>
      </tr>
    </>
  );
};

const DateFilter = ({ onFilterChange, selectedDate }) => {
  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full">
      <label htmlFor="dateFilter" className="text-gray-600 whitespace-nowrap text-sm sm:text-base">
        Filter by Date:
      </label>
      <div className="relative w-full max-w-[180px]">
        <DatePicker
          selected={selectedDate}
          onChange={onFilterChange}
          dateFormat="dd-MM-yyyy"
          className="border rounded pl-3 pr-8 py-2 w-full text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholderText="Select date to filter"
        />
        {selectedDate && (
          <button
            onClick={() => onFilterChange(null)}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-sm w-4 h-4 flex items-center justify-center"
            aria-label="Clear date"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};
// Add this new component at the top level, after other component definitions
const ConfirmationModal = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
        <h3 className="text-lg font-semibold mb-4">Confirm</h3>
        <p className="text-gray-600 mb-6">Are you really want to change to completed status?</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 transition-colors"
          >
            No
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            Yes
          </button>
        </div>
      </div>
    </div>
  );
};

const DoctorDetails = () => {
  // Add this with other state declarations at the top of the component
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    index: null,
    pendingStatus: null
  });

  const { id } = useParams();
  const [doctorInfo, setDoctorInfo] = useState(null);
  const { loading, error, doctorDetails, fetchDoctorDetails, doctors } = useDoctor();
  const [updatedAppointments, setUpdatedAppointments] = useState([]);
  // const [isUpdating, setIsUpdating] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState({});
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [filterDate, setFilterDate] = useState(null);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [isUpdatingSingle, setIsUpdatingSingle] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchDoctorDetails(id);
    fetchDoctorInfo();
  }, [id]);

  const fetchDoctorInfo = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found");
      const response = await fetch(
        apiUrl + `/api/auth/doctors/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch doctor info");
      }

      const data = await response.json();
      setDoctorInfo(data);
    } catch (error) {
      console.error("Error fetching doctor info:", error);
    }
  };

  useEffect(() => {
    if (!filterDate) {
      setFilteredAppointments([...updatedAppointments]);
    } else {
      const filtered = updatedAppointments.filter((appointment) => {
        const appointmentDate = new Date(appointment.date);
        return appointmentDate.toDateString() === filterDate.toDateString();
      });
      setFilteredAppointments(filtered);
    }
  }, [filterDate, updatedAppointments]);

  useEffect(() => {
    if (doctorDetails?.appointments) {
      // Sort appointments by date
      const sortedAppointments = [...doctorDetails.appointments].sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB - dateA;
      });

      setUpdatedAppointments(sortedAppointments);
      setFilteredAppointments(sortedAppointments);

      // Group appointments by date
      const appointmentsByDate = sortedAppointments.reduce((acc, appointment) => {
        if (appointment.date) {
          const date = new Date(appointment.date).toDateString();
          if (!acc[date]) {
            acc[date] = [];
          }
          acc[date].push(appointment);
        }
        return acc;
      }, {});

      // Fetch availability for each unique date
      Object.keys(appointmentsByDate).forEach(date => {
        fetchDoctorAvailability(id, new Date(date));
      });
    }
  }, [doctorDetails]);

  // Add a new state for individual loading states
  const [loadingSlotIndices, setLoadingSlotIndices] = useState({});

  const fetchDoctorAvailability = async (doctorId, selectedDate) => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        setAvailableTimeSlots(prev => ({
          ...prev,
          [selectedDate.toDateString()]: []
        }));
        return;
      }

      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${apiUrl}/api/appointment/doctor/availability/${doctorId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch availability");
      }

      setAvailableTimeSlots(prev => ({
        ...prev,
        [selectedDate.toDateString()]: data.availableSlots || []
      }));

    } catch (error) {
      console.error("Error fetching availability:", error);
      setAvailableTimeSlots(prev => ({
        ...prev,
        [selectedDate.toDateString()]: []
      }));
    } finally {
      setIsLoadingSlots(false);
    }
  };

  // Add this function for single appointment updates
  const handleUpdateSingleAppointment = async (index) => {
    setIsUpdatingSingle(index);
    setFieldErrors({}); // Clear previous errors
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const appointment = updatedAppointments[index];

      // Validate fields
      const errors = {};
      if (!appointment.latitude) errors.latitude = "Latitude is required";
      if (!appointment.longitude) errors.longitude = "Longitude is required";

      if (Object.keys(errors).length > 0) {
        setFieldErrors({ ...errors, index });
        return;
      }

      const originalAppointment = doctorDetails.appointments.find(a => a._id === appointment._id);

      // Check if there are actually changes
      const hasChanges = (
        appointment.checkIn !== originalAppointment.checkIn ||
        appointment.startTime !== originalAppointment.startTime ||
        appointment.endTime !== originalAppointment.endTime ||
        appointment.status !== originalAppointment.status ||
        appointment.latitude !== originalAppointment.latitude ||
        appointment.longitude !== originalAppointment.longitude
      );

      if (!hasChanges) {
        alert("No changes to update for this appointment");
        return;
      }

      const response = await fetch(
        `${apiUrl}/api/admin/update/${appointment._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            checkIn: appointment.checkIn,
            startTime: appointment.startTime,
            endTime: appointment.endTime,
            status: appointment.status,
            latitude: appointment.latitude,
            longitude: appointment.longitude,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update appointment ${appointment._id}`);
      }

      // Refresh data
      await fetchDoctorDetails(id);
    } catch (err) {
      console.error("Update error:", err);
      alert("Failed to update appointment. See console for details.");
    } finally {
      setIsUpdatingSingle(null);
    }
  };

  const getDoctorImage = (gender) => {
    const currentDoctor = doctors.find((doc) => doc.id === id);
    return currentDoctor?.gender === "Female"
      ? "https://img.freepik.com/free-vector/doctor-background-design_1270-62.jpg"
      : "https://img.freepik.com/free-vector/doctor-background-design_1270-84.jpg";
  };

  const activeDays = ["S", "M", "T", "W", "T"];

  const handleLocationChange = (index, field, value) => {
    const newAppointments = [...updatedAppointments];
    newAppointments[index] = {
      ...newAppointments[index],
      [field]: value,
    };
    setUpdatedAppointments(newAppointments);
  };


  const handleCheckInChange = (index) => {
    const newAppointments = [...updatedAppointments];
    newAppointments[index] = {
      ...newAppointments[index],
      checkIn: !newAppointments[index].checkIn,
    };
    setUpdatedAppointments(newAppointments);
  };

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleDateChange = async (index, date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) return;

    const newAppointments = [...updatedAppointments];
    const formattedDate = date.toISOString().split("T")[0];

    newAppointments[index] = {
      ...newAppointments[index],
      date: formattedDate,
      startTime: "",
      endTime: "",
    };

    setUpdatedAppointments(newAppointments);
    await fetchDoctorAvailability(id, date);
  };

  // Update the handleStatusChange function
  // Add this function before handleStatusChange
  const updateAppointmentStatus = (index, value) => {
    const newAppointments = [...updatedAppointments];
    newAppointments[index] = {
      ...newAppointments[index],
      status: value,
    };
    setUpdatedAppointments(newAppointments);
  };

  // Existing handleStatusChange function
  const handleStatusChange = (index, value) => {
    if (value === "Completed") {
      setConfirmationModal({
        isOpen: true,
        index,
        pendingStatus: value
      });
    } else {
      updateAppointmentStatus(index, value);
    }
  };

  const renderTimeSlotDropdown = (appointment, index) => {
    const appointmentDate = appointment.date ? new Date(appointment.date) : null;

    if (appointmentDate && (isPastDate(appointmentDate) || appointment.status === "Completed")) {
      return (
        <input
          type="text"
          value={appointment.startTime || ""}
          readOnly
          className="border rounded px-2 py-1 w-24 bg-gray-100"
          placeholder="Start Time"
        />
      );
    }

    if (isLoadingSlots) {
      return <div className="text-gray-500">Loading slots...</div>;
    }

    const slotsForThisAppointment = availableTimeSlots[appointmentDate?.toDateString()] || [];

    // Filter slots based on current time for today's appointments
    const filteredSlots = slotsForThisAppointment.filter(slot => {
      if (!appointmentDate) return true;

      const today = new Date();
      const isToday = appointmentDate.toDateString() === today.toDateString();

      if (!isToday) return true;

      // Convert slot time to Date object for comparison
      const [time, period] = slot.start.split(' ');
      let [hours, minutes] = time.split(':');
      hours = parseInt(hours);

      // Convert to 24-hour format for comparison
      if (period === 'PM' && hours !== 12) {
        hours += 12;
      } else if (period === 'AM' && hours === 12) {
        hours = 0;
      }

      const slotTime = new Date();
      slotTime.setHours(hours, parseInt(minutes), 0, 0);

      const currentTime = new Date();

      return slotTime > currentTime;
    });

    if (filteredSlots.length === 0) {
      return <div className="text-gray-500">No available slots</div>;
    }

    // Find if the current startTime exists in available slots
    const currentTimeExists = filteredSlots.some(
      slot => slot.start === appointment.startTime
    );

    return (
      <div className="relative">
        <select
          value={appointment.startTime || ""}
          onClick={() => {
            // Fetch new slots when dropdown is clicked
            fetchDoctorAvailability(id, appointmentDate);
          }}
          onChange={(e) => {
            const newAppointments = [...updatedAppointments];
            const selectedSlot = filteredSlots.find(
              (slot) => slot.start === e.target.value
            );

            newAppointments[index] = {
              ...newAppointments[index],
              startTime: selectedSlot ? selectedSlot.start : "",
              endTime: selectedSlot ? selectedSlot.end : "",
            };

            setUpdatedAppointments(newAppointments);
          }}
          className="border rounded px-2 py-1 w-24 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {/* Show current time as first option if it exists but isn't in available slots */}
          {appointment.startTime && !currentTimeExists && (
            <option value={appointment.startTime}>
              {appointment.startTime}
            </option>
          )}

          {/* Show "Select time" only if no time is selected */}
          {!appointment.startTime && <option value="">Select time</option>}

          {/* Available time slots */}
          {filteredSlots.map((slot) => (
            <option key={slot.start} value={slot.start}>
              {slot.start}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const handleFilterChange = (date) => {
    setFilterDate(date);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!doctorDetails) return <div>No doctor found</div>;

  return (
    <div className="p-3 sm:p-6">
      {/* Add the modal component right after the opening div */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ isOpen: false, index: null, pendingStatus: null })}
        onConfirm={() => {
          updateAppointmentStatus(confirmationModal.index, confirmationModal.pendingStatus);
          setConfirmationModal({ isOpen: false, index: null, pendingStatus: null });
        }}
      />

      <Link
        to="/dashboard"
        className="flex items-center mb-4 sm:mb-6"
        style={{ color: "#8D1B3D" }}
      >
        <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
        Back to Dashboard
      </Link>


      <div className="bg-white rounded-lg shadow-lg p-4 sm:p-5 flex flex-col gap-2">
        <div className="flex justify-between items-start">
          <div className="flex gap-5">
            <img
              src={getDoctorImage(doctorInfo?.gender)}
              alt={doctorInfo?.doctorName}
              className="w-40 h-40 rounded-xl object-cover"
            />
            <div>
              <h3 className="font-bold text-md text-gray-900">
                Dr. {doctorInfo?.doctorName}
              </h3>
              <div className="text-xs text-[#8D1B3D] font-medium flex items-center gap-1 bg-[#F9EDF1] px-2 py-0.5 rounded-full w-fit mt-1">
                {doctorInfo?.clinicName || "Unknown"} away
              </div>
              <div className="mt-1">
                <p className="text-sm font-medium text-[#8D1B3D]">
                  {doctorInfo?.specialization}
                </p>
                <p className="text-xs text-gray-500">{doctorInfo?.clinicName}</p>
              </div>

              <div className="mt-2 flex items-center justify-between">
                <div className="flex gap-1">
                  {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
                    <span
                      key={index}
                      className={`text-xs px-1.5 py-0.5 rounded-md border ${activeDays.includes(day)
                        ? "bg-[#8D1B3D] text-white"
                        : "text-gray-400 bg-gray-100"
                        }`}
                    >
                      {day}
                    </span>
                  ))}
                </div>
              </div>
            </div>


          </div>

          <div className="text-right">
            <div className="flex items-center gap-1 text-sm text-gray-800">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span>{doctorInfo?.averageRating?.toFixed(1) || "4.3"}</span>
            </div>
            <div className="text-xs text-gray-400">
              {doctorInfo?.reviewCount || 140} reviews
            </div>
          </div>
        </div>


      </div>

      <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <h2 className="text-lg sm:text-xl font-semibold">Patient Appointments</h2>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
            <DateFilter onFilterChange={handleFilterChange} selectedDate={filterDate} />
          </div>
        </div>
        <div className="overflow-x-auto -mx-3 sm:mx-0">
          <div className="min-w-full inline-block align-middle">
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="hidden sm:table-header-group">
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">PATIENT</th>
                    <th className="text-left py-3 px-4">DATE</th>
                    <th className="text-left py-3 px-4">START TIME</th>
                    <th className="text-left py-3 px-4">END TIME</th>
                    <th className="text-left py-3 px-4">LATITUDE</th>
                    <th className="text-left py-3 px-4">LONGITUDE</th>
                    <th className="text-left py-3 px-4">CHECK-IN</th>
                    <th className="text-left py-3 px-4">STATUS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredAppointments.map((appointment, index) => (
                    <SortableItem
                      key={appointment._id}
                      id={appointment._id}
                      appointment={appointment}
                      index={index}
                      renderTimeSlotDropdown={renderTimeSlotDropdown}
                      handleDateChange={handleDateChange}
                      handleCheckInChange={handleCheckInChange}
                      handleStatusChange={handleStatusChange}
                      handleLocationChange={handleLocationChange}
                      isPastDate={isPastDate}
                      convertTo12Hour={(time) => time}
                      previousAppointmentDate={index > 0 ? new Date(filteredAppointments[index - 1].date) : null}
                      isFirstAppointment={index === 0} // Add this prop
                      handleUpdateSingleAppointment={handleUpdateSingleAppointment}
                      isUpdatingSingle={isUpdatingSingle}
                      fieldErrors={fieldErrors}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorDetails;
